import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import TypeBadge from '../components/TypeBadge';
import AssetCover from '../components/AssetCover';
import VerifyBadge from '../components/VerifyBadge';
import AssetPreview from '../components/AssetPreview';
import AssetApiTab from '../components/AssetApiTab';
import { useAuth } from '../context/AuthContext';
import api from '../api/ApiClient';

function Tab({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer',
      fontSize: 13, fontWeight: active ? 600 : 400,
      color: active ? 'var(--accent)' : 'var(--text-2)',
      borderBottom: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
      fontFamily: 'inherit', transition: 'all 0.15s',
    }}>
      {label}
    </button>
  );
}

function Stars({ value }) {
  const r = Math.round(value || 0);
  return <span style={{ color: '#F59E0B' }}>{[1,2,3,4,5].map(i => i <= r ? '★' : '☆').join('')}</span>;
}

function FileLock({ price }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '5px 12px', borderRadius: 'var(--radius-sm)',
      background: 'var(--surface2)', border: '1px solid var(--border)',
      fontSize: 12, color: 'var(--text-3)',
    }}>
      <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
        <path d="M11 7V5a3 3 0 0 0-6 0v2H3v8h10V7h-2zm-4-2a1 1 0 0 1 2 0v2H7V5z"/>
      </svg>
      Purchase required {price > 0 && `· $${price}`}
    </div>
  );
}

export default function AssetDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [versions, setVersions] = useState(null);
  const [buying, setBuying] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSent, setReviewSent] = useState(false);
  const [error, setError] = useState('');
  const [bought, setBought] = useState(false);
  const [userApiKey, setUserApiKey] = useState('');

  const loadAsset = () =>
    api.getAsset(id)
      .then(d => { setAsset(d.asset); })
      .catch(() => setError('Asset not found'))
      .finally(() => setLoading(false));

  useEffect(() => { loadAsset(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if user already has this asset + load their first API key
  useEffect(() => {
    if (!user || !asset) return;
    api.getOrders('bought').then(d => {
      const has = (d.orders || []).some(o => String(o.assetId?._id || o.assetId) === id && o.status === 'completed');
      setBought(has);
    }).catch(() => {});
    api.listApiKeys().then(d => {
      const first = d.keys?.[0];
      if (first) setUserApiKey(first.keyPrefix);
    }).catch(() => {});
  }, [user, asset, id]);

  const handleBuy = async () => {
    if (!user) { navigate('/login'); return; }
    setBuying(true);
    setError('');
    try {
      await api.createOrder({ assetId: id });
      setBought(true);
      await loadAsset();
    } catch (err) {
      setError(err?.response?.data?.message || 'Purchase failed');
    }
    setBuying(false);
  };

  const handleDownload = (assetId, fileId, fileName) => {
    const url = api.getDownloadUrl(assetId, fileId);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'download';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleReview = async (e) => {
    e.preventDefault();
    try {
      await api.addReview(id, { rating: reviewRating, comment: reviewComment });
      setReviewSent(true);
      await loadAsset();
    } catch (err) {
      setError(err?.response?.data?.message || 'Review failed');
    }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)' }}>
      Loading…
    </div>
  );

  if (!asset) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ color: 'var(--text-2)', marginBottom: 12 }}>{error || 'Asset not found'}</p>
        <Link to="/" style={{ color: 'var(--accent)' }}>Back to marketplace</Link>
      </div>
    </div>
  );

  const owner = asset.ownerId || {};
  const stats = asset.stats || {};
  const isFree = asset.price === 0;
  const isOwner = user && String(owner._id) === user.id;
  const hasAccess = isFree || bought || isOwner;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Navbar */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(8px)', borderBottom: '1px solid var(--border)',
        padding: '0 24px', height: 52, display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Link to="/" style={{ fontWeight: 700, fontSize: 17, color: 'var(--text)', textDecoration: 'none', letterSpacing: '-0.3px' }}>dApiary</Link>
        <span style={{ color: 'var(--text-3)' }}>/</span>
        <span style={{ fontSize: 13, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>{asset.title}</span>
      </header>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px', display: 'grid', gridTemplateColumns: '1fr 320px', gap: 28, alignItems: 'start' }}>

        {/* ── LEFT ─────────────────────────────────────────────────────────── */}
        <div>
          {/* Cover */}
          <div style={{ borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 24 }}>
            <AssetCover assetType={asset.assetType} previewImage={asset.previewImage} height={200} />
          </div>

          {/* Badges + title */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            <TypeBadge type={asset.assetType} size="md" />
            {(asset.status === 'verified' || asset.status === 'published') && <VerifyBadge size="md" />}
            {asset.languages?.map(l => (
              <span key={l} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: 'var(--accent-bg)', color: 'var(--accent)', fontFamily: 'Space Mono', fontWeight: 700, textTransform: 'uppercase' }}>{l}</span>
            ))}
          </div>

          <h1 style={{ fontSize: 26, fontWeight: 700, color: 'var(--text)', marginBottom: 10, letterSpacing: '-0.5px' }}>
            {asset.title}
            <span style={{ marginLeft: 10, fontSize: 13, fontFamily: 'Space Mono', fontWeight: 400, color: 'var(--text-3)', verticalAlign: 'middle' }}>
              v{asset.currentVersion || '1.0.0'}
            </span>
          </h1>

          <div style={{ display: 'flex', gap: 18, color: 'var(--text-3)', fontSize: 13, marginBottom: 24 }}>
            <span><Stars value={stats.rating} /> {(stats.rating || 0).toFixed(1)} ({stats.reviewCount || 0})</span>
            <span>{stats.downloads || 0} downloads</span>
            <span>{stats.views || 0} views</span>
          </div>

          {/* Tabs */}
          <div style={{ borderBottom: '1px solid var(--border)', marginBottom: 24, display: 'flex' }}>
            <Tab label="Overview" active={tab === 'overview'} onClick={() => setTab('overview')} />
            <Tab label="Preview" active={tab === 'preview'} onClick={() => setTab('preview')} />
            <Tab label={`Reviews (${stats.reviewCount || 0})`} active={tab === 'reviews'} onClick={() => setTab('reviews')} />
            {['dataset','llm','cv_model','audio_model','image_model','embedding','other_model'].includes(asset.assetType) && (
              <Tab label="Use via API" active={tab === 'api'} onClick={() => setTab('api')} />
            )}
            <Tab label="Versions" active={tab === 'versions'} onClick={() => {
              setTab('versions');
              if (!versions) api.listVersions(id).then(d => setVersions(d)).catch(() => setVersions({ versions: [] }));
            }} />
          </div>

          {/* Tab: Overview */}
          {tab === 'overview' && (
            <div>
              {asset.shortDescription && (
                <p style={{ color: 'var(--text-2)', lineHeight: 1.7, marginBottom: 20, fontSize: 15 }}>{asset.shortDescription}</p>
              )}
              {asset.fullDescription && (
                <div style={{ color: 'var(--text)', lineHeight: 1.8, fontSize: 14, whiteSpace: 'pre-wrap', marginBottom: 24 }}>
                  {asset.fullDescription}
                </div>
              )}

              {/* Metadata table */}
              {(() => {
                const rows = [
                  ['License', asset.license], ['Category', asset.category],
                  ['Tags', asset.tags?.join(', ')],
                  ...(asset.datasetMeta ? [
                    ['Rows', asset.datasetMeta.rowCount?.toLocaleString()],
                    ['Collection', asset.datasetMeta.collectionMethod],
                    ['Annotation', asset.datasetMeta.annotationType],
                  ] : []),
                  ...(asset.modelMeta ? [
                    ['Architecture', asset.modelMeta.architecture],
                    ['Parameters', asset.modelMeta.parameters],
                    ['Framework', asset.modelMeta.framework],
                    ['Task', asset.modelMeta.taskType],
                    ['Quantization', asset.modelMeta.quantization],
                  ] : []),
                ].filter(([, v]) => v);
                return rows.length > 0 && (
                  <div>
                    <h3 style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Details</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {rows.map(([k, v]) => (
                        <div key={k} style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: '9px 13px', borderRadius: 'var(--radius-sm)' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{k}</div>
                          <div style={{ fontSize: 13, color: 'var(--text)', fontFamily: 'Space Mono' }}>{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Tab: Preview */}
          {tab === 'preview' && <AssetPreview asset={asset} />}

          {/* Tab: Use via API */}
          {tab === 'api' && <AssetApiTab asset={asset} userApiKey={userApiKey} />}

          {/* Tab: Versions */}
          {tab === 'versions' && (
            <div>
              {/* Current */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderRadius: 8, background: 'var(--accent-bg)', border: '1px solid #BFDBFE', marginBottom: 16 }}>
                <span style={{ fontSize: 13, fontFamily: 'Space Mono', fontWeight: 700, color: 'var(--accent)' }}>v{asset.currentVersion || '1.0.0'}</span>
                <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>Current</span>
                <span style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 'auto' }}>{new Date(asset.updatedAt).toLocaleDateString()}</span>
              </div>

              {/* History */}
              {!versions && <p style={{ color: 'var(--text-3)', fontSize: 13 }}>Loading…</p>}
              {versions && (versions.versions || []).length === 0 && (
                <p style={{ color: 'var(--text-3)', fontSize: 13 }}>No previous versions. Release a new version from My Assets to create version history.</p>
              )}
              {versions && [...(versions.versions || [])].reverse().map((v, i) => (
                <div key={i} className="card" style={{ padding: '14px 16px', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: v.changelog ? 8 : 0 }}>
                    <span style={{ fontSize: 13, fontFamily: 'Space Mono', fontWeight: 700, color: 'var(--text)' }}>v{v.version}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 'auto' }}>{new Date(v.createdAt).toLocaleDateString()}</span>
                  </div>
                  {v.changelog && <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0, lineHeight: 1.6 }}>{v.changelog}</p>}
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>{(v.files || []).length} file(s)</div>
                </div>
              ))}
            </div>
          )}

          {/* Tab: Reviews */}
          {tab === 'reviews' && (
            <div>
              {(asset.reviews || []).length === 0 && (
                <p style={{ color: 'var(--text-3)', fontSize: 14 }}>No reviews yet.</p>
              )}
              {(asset.reviews || []).map((r, i) => (
                <div key={i} className="card" style={{ padding: 16, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <Stars value={r.rating} />
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{new Date(r.createdAt).toLocaleDateString()}</span>
                  </div>
                  <p style={{ color: 'var(--text-2)', fontSize: 14, margin: 0, lineHeight: 1.6 }}>{r.comment}</p>
                </div>
              ))}

              {user && bought && !reviewSent && (
                <form onSubmit={handleReview} className="card" style={{ padding: 20, marginTop: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 14 }}>Write a review</h4>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
                    {[1,2,3,4,5].map(s => (
                      <button key={s} type="button" onClick={() => setReviewRating(s)} style={{
                        background: 'none', border: 'none', cursor: 'pointer', fontSize: 22,
                        color: s <= reviewRating ? '#F59E0B' : '#CBD5E1', padding: 2,
                      }}>★</button>
                    ))}
                  </div>
                  <textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)}
                    placeholder="Share your experience…" rows={3} className="field"
                    style={{ width: '100%', resize: 'vertical', marginBottom: 10 }} />
                  <button type="submit" className="btn-primary" style={{ fontSize: 13 }}>Submit review</button>
                </form>
              )}
              {reviewSent && <p style={{ color: 'var(--success)', marginTop: 12, fontSize: 13 }}>Review submitted.</p>}
            </div>
          )}
        </div>

        {/* ── RIGHT ────────────────────────────────────────────────────────── */}
        <div style={{ position: 'sticky', top: 68 }}>
          <div className="card" style={{ padding: 24, marginBottom: 16 }}>
            {/* Price */}
            <div style={{ fontSize: 28, fontWeight: 700, color: isFree ? 'var(--success)' : 'var(--text)', marginBottom: 16, fontFamily: 'Space Mono', letterSpacing: '-1px' }}>
              {isFree ? 'Free' : `$${asset.price}`}
            </div>

            {error && (
              <div style={{ padding: '9px 13px', borderRadius: 'var(--radius-sm)', background: 'var(--danger-bg)', color: 'var(--danger)', fontSize: 13, marginBottom: 12, border: '1px solid #FECACA' }}>
                {error}
              </div>
            )}

            {/* CTA */}
            {hasAccess ? (
              <div style={{ padding: '9px 13px', borderRadius: 'var(--radius-sm)', background: 'var(--success-bg)', color: 'var(--success)', fontSize: 13, fontWeight: 500, border: '1px solid #bbf7d0', textAlign: 'center' }}>
                {isOwner ? 'Your asset' : isFree ? 'Free — download below' : 'Purchased'}
              </div>
            ) : (
              <button onClick={handleBuy} disabled={buying} className="btn-primary" style={{ width: '100%', padding: 11, fontSize: 14 }}>
                {buying ? 'Processing…' : `Buy for $${asset.price}`}
              </button>
            )}

            {/* Files list */}
            {asset.files?.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <h4 style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Files</h4>
                {asset.files.map((f, i) => {
                  const accessible = !f.isPrivate || hasAccess;
                  const fileUrl = accessible ? f.fileUrl : null;
                  const isGridFS = fileUrl && /^[0-9a-f]{24}$/i.test(fileUrl);
                  const isLink = fileUrl && !isGridFS;

                  return (
                    <div key={f._id || i} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 0', borderBottom: '1px solid var(--border)',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.label || 'File'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'Space Mono', marginTop: 1 }}>
                          {f.format?.toUpperCase()}{f.sizeBytes > 0 ? ` · ${(f.sizeBytes / 1024 / 1024).toFixed(1)} MB` : ''}
                        </div>
                      </div>

                      {accessible ? (
                        isGridFS ? (
                          <button
                            onClick={() => handleDownload(asset._id, f._id, f.label)}
                            style={{ flexShrink: 0, background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--accent)', fontWeight: 500 }}
                          >
                            Download
                          </button>
                        ) : isLink ? (
                          <a href={fileUrl} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0, fontSize: 12, color: 'var(--accent)', textDecoration: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '4px 10px' }}>
                            Open link
                          </a>
                        ) : null
                      ) : (
                        <FileLock price={asset.price} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Seller */}
          <div className="card" style={{ padding: 20 }}>
            <h4 style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Seller</h4>
            <Link to={`/seller/${owner._id}`} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--accent-bg)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
                {owner.avatar
                  ? <img src={owner.avatar} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                  : (owner.name || 'U')[0]
                }
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                  {owner.name}
                  {owner.sellerStats?.isVerifiedSeller && (
                    <span style={{ marginLeft: 6, fontSize: 11, padding: '1px 6px', borderRadius: 3, background: 'var(--success-bg)', color: 'var(--success)', fontWeight: 600 }}>Verified</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                  {(owner.sellerStats?.rating || 0).toFixed(1)} rating · {owner.sellerStats?.totalSales || 0} sales
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
